import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useEffect, useMemo, useState, type ButtonHTMLAttributes, type CSSProperties } from 'react'
import clsx from 'clsx'
import { api } from '../lib/api'
import { Users, Calendar, BookOpen, FileText, Clock, TrendingUp, CheckSquare, FileCheck, Trophy, Gamepad2, Crown, RefreshCw, CalendarDays, User, Mail, FolderOpen, ShieldQuestion, GripVertical, Plus, X } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth'
import { format, parseISO } from 'date-fns'
import { de } from 'date-fns/locale'
import { loadStudentPhotos, createPhotoDataUrl } from '../lib/photoUtils'
import { KiHubUsageCard } from '../components/KiHubUsageCard'
import { DashboardTimetableWidget } from '../components/DashboardTimetableWidget'
import {
  DASHBOARD_WIDGET_TITLE_BY_ID,
  getDefaultWidgetOrder,
  listAddableWidgets,
  mergeWidgetOrderWithDefaults,
  type DashboardWidgetAvailability,
  type DashboardWidgetId
} from '../lib/dashboardWidgetLayout'
import {
  Badge,
  Button,
  Chip,
  ChipList,
  EmptyState,
  ListToolbar,
  Modal,
  SortableListItem,
  SortableVerticalList,
  Tile,
  TileContent,
  TileDescription,
  TileFooter,
  TileHeader,
  TileTitle
} from '../components/ui'

/** Link wie Showcase-`Button` outline sm (kein verschachteltes `<button>`). */
const linkOutlineSmClass =
  'btn btn-outline btn-sm inline-flex items-center justify-center gap-2 font-medium no-underline'

interface DashboardStats {
  totalStudents: number
  activeSessions: number
  buildingBlocks: number
  learningPlans: number
  totalTasks: number
  totalEvidence: number
}

interface Session {
  SessionID: number
  Lehrer: string
  Fach: string
  TimeStamp: string
  studentCount: number
}

interface BuildingBlock {
  BID: number
  NAME: string
  FACH: string
  STUFE: string
  JAHR: number
  START_DATUM: string
  END_DATUM: string
}

interface LearningPlan {
  LPID: number
  NAME: string
  FACH: string
  STUFE: string
  JAHR: number
  STATUS: string
  START_DATUM: string
  END_DATUM: string
}

interface HighscoreEntry {
  username: string
  score: number
  maxLength?: number
  durationSeconds?: number
  createdAt?: string
  updatedAt?: string
  classes?: string[]
  correctAnswers?: number
  livesRemaining?: number
  totalStudents?: number
}

interface UserEmailCredential {
  id?: number
  email_address: string
  enabled: boolean
}

interface EmailLabel {
  id: number
  name: string
  color?: string
  icon?: string
}

interface DashboardEmailSummary {
  id?: number
  uid?: number
  subject: string
  from: string
  date: Date
  isUnread: boolean
  llmSummary?: string
  labels?: EmailLabel[]
}

interface ClassRankingStudentSummary {
  id: string
  name: string
  progress: number
}

interface ClassRankingEntry {
  className: string
  studentCount: number
  averageProgress: number
  averageBaseProgress: number
  averageBonusProgress: number
  averageLearningPlans: number
  regularTaskCompletionRate: number
  bonusTaskCompletionRate: number
  topStudents: ClassRankingStudentSummary[]
  trendBaseline: number | null
  trendDelta: number
}

interface ClassRankingResponse {
  success: boolean
  data: ClassRankingEntry[]
  calculatedAt: string
  validUntil: string
  cached: boolean
  source?: string
  skipped?: boolean
  reason?: string
}

const extractHighscoreData = (payload: any): HighscoreEntry[] => {
  if (!payload) {
    return []
  }

  if (Array.isArray(payload)) {
    return payload as HighscoreEntry[]
  }

  if (Array.isArray(payload?.data)) {
    return payload.data as HighscoreEntry[]
  }

  if (Array.isArray(payload?.highscores)) {
    return payload.highscores as HighscoreEntry[]
  }

  return []
}

export default function Dashboard() {
  const decodeRFC2047 = (str: string): string => {
    if (!str) return str
    return str.replace(/=\?([^?]+)\?([BQbq])\?([^?]+)\?=/g, (match, charset, encoding, encodedText) => {
      try {
        if (encoding.toUpperCase() === 'Q') {
          const decoded = encodedText.replace(/_/g, ' ').replace(/=([0-9A-F]{2})/gi, (_: string, hex: string) => {
            return String.fromCharCode(parseInt(hex, 16))
          })
          try {
            return decodeURIComponent(escape(decoded))
          } catch {
            return decoded
          }
        }

        if (encoding.toUpperCase() === 'B') {
          try {
            const base64Text = encodedText.replace(/\s/g, '')
            const binaryString = atob(base64Text)

            const bytes = new Uint8Array(binaryString.length)
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i)
            }

            if (charset.toUpperCase() === 'UTF-8') {
              try {
                const decoder = new TextDecoder('utf-8')
                return decoder.decode(bytes)
              } catch {
                return decodeURIComponent(escape(binaryString))
              }
            }

            return binaryString
          } catch {
            return match
          }
        }
      } catch {
        return match
      }

      return match
    })
  }

  const { data: stats, isLoading } = useQuery<DashboardStats>(
    'dashboard/stats',
    async () => {
      const [studentCount, todaySessions, allSessions, buildingBlockCount, learningPlanCount, taskCount, evidenceCount] = await Promise.all([
        api.get('/students/count').then(res => res.data.data?.total ?? 0),
        api.get('/lernbuero/sessions/today').then(res => res.data.data),
        api.get('/sessions').then(res => res.data.data),
        api.get('/building-blocks/count').then(res => res.data.data?.total ?? 0),
        api.get('/learning-plans/count').then(res => res.data.data?.total ?? 0),
        api.get('/tasks/count').then(res => res.data.data?.total ?? 0),
        api.get('/evidence/count').then(res => res.data.data?.total ?? 0),
      ])
      
      return {
        totalStudents: studentCount,
        activeSessions: todaySessions.length, // Only today's sessions
        buildingBlocks: buildingBlockCount,
        learningPlans: learningPlanCount,
        totalTasks: taskCount,
        totalEvidence: evidenceCount,
      }
    }
  )

  // Fetch detailed data for dashboard sections
  const { data: activeSessions } = useQuery<Session[]>(
    'dashboard/active-sessions',
    async () => {
      // Use the Lernbuero endpoint which includes actual student count
      const response = await api.get('/lernbuero/sessions/today')
      return response.data.data.slice(0, 5) // Limit to 5 sessions
    }
  )

  const { data: recentBuildingBlocks } = useQuery<BuildingBlock[]>(
    'dashboard/building-blocks',
    async () => {
      const response = await api.get('/building-blocks', {
        params: { limit: 5 }
      })
      return response.data.data
    }
  )

  const { data: recentLearningPlans } = useQuery<LearningPlan[]>(
    'dashboard/learning-plans',
    async () => {
      const response = await api.get('/learning-plans', {
        params: { limit: 5 }
      })
      return response.data.data
    }
  )

  // Fetch snake highscores for dashboard
  const { data: snakeHighscores } = useQuery<HighscoreEntry[]>(
    'dashboard/snake-highscores',
    async () => {
      try {
        const response = await api.get('/namen-lernen/snake/highscores')
        return response.data.data?.slice(0, 5) || [] // Top 5 highscores
      } catch (error) {
        console.log('No snake highscores available yet')
        return []
      }
    }
  )

  const { data: quizHighscores } = useQuery<HighscoreEntry[]>(
    'dashboard/quiz-highscores',
    async () => {
      try {
        const response = await api.get('/namen-lernen/quiz/highscores')
        const highscores = extractHighscoreData(response.data)
        return highscores.slice(0, 5)
      } catch (error) {
        console.log('No quiz highscores available yet')
        return []
      }
    }
  )

  const {
    data: classRanking,
    isLoading: isClassRankingLoading,
  } = useQuery<ClassRankingResponse>(
    'dashboard/class-ranking',
    async () => {
      try {
        const response = await api.get('/statistics/class-ranking')
        return response.data as ClassRankingResponse
      } catch (error) {
        console.error('Failed to load class ranking:', error)
        return {
          success: false,
          data: [],
          calculatedAt: '',
          validUntil: '',
          cached: false,
          source: 'error',
        }
      }
    },
    {
      staleTime: 60 * 60 * 1000,
      cacheTime: 60 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  )

  const queryClient = useQueryClient()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isWidgetCatalogOpen, setIsWidgetCatalogOpen] = useState(false)

  const { data: dashboardLayout } = useQuery<{ widgetOrder?: string[] | null }>(
    'dashboard/widget-layout',
    async () => {
      const response = await api.get('/profile/dashboard-layout')
      return response.data?.data || { widgetOrder: null }
    },
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  )

  // Fetch upcoming appointments
  const { data: upcomingAppointments, isLoading: upcomingAppointmentsLoading } = useQuery(
    'dashboard/upcoming-appointments',
    async () => {
      try {
        const response = await api.get('/appointments/upcoming?limit=6')
        return response.data.data || []
      } catch (error) {
        console.error('Failed to load upcoming appointments:', error)
        return []
      }
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  )

  const { data: emailCredentials } = useQuery<UserEmailCredential[]>(
    'dashboard/email-credentials',
    async () => {
      try {
        const response = await api.get('/email/credentials')
        if (response.data?.success) {
          return response.data.data || []
        }
      } catch (error) {
        console.error('Failed to load email credentials:', error)
      }
      return []
    },
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  )

  const hasEmailAddress = (emailCredentials ?? []).some(
    (credential) => Boolean(credential.email_address?.trim()) && credential.enabled
  )

  const { data: importantEmails, isLoading: importantEmailsLoading } = useQuery<DashboardEmailSummary[]>(
    'dashboard/important-emails',
    async () => {
      const response = await api.get('/email-client/inbox', {
        params: {
          limit: 30,
          offset: 0,
          searchQuery: 'ALL',
          box: 'INBOX',
        },
      })
      const emails = response.data?.data || []
      const emailIds = emails.map((email: any) => email.id).filter((id: any) => Boolean(id))
      const labelsByEmailId: Record<number, EmailLabel[]> = {}

      if (emailIds.length > 0) {
        const labelResults = await Promise.all(
          emailIds.map(async (emailId: number) => {
            try {
              const labelsResponse = await api.get(`/email-client-advanced/emails/${emailId}/labels`)
              return { emailId, labels: labelsResponse.data?.data || [] }
            } catch (error) {
              console.error('Failed to load labels for email:', emailId, error)
              return { emailId, labels: [] }
            }
          })
        )

        labelResults.forEach(({ emailId, labels }) => {
          labelsByEmailId[emailId] = labels
        })
      }

      const mapped = emails.map((email: any) => {
        const headers = email.headers || {}
        const subject = headers.subject || email.subject || '(Kein Betreff)'
        const from = headers.from || email.from || 'Unbekannter Absender'
        const rawDate = headers.date || email.date || email.internalDate
        const date = rawDate ? new Date(rawDate) : new Date()
        const flags = Array.isArray(email.flags) ? email.flags : []
        const isUnread = !flags.some((flag: any) => String(flag).toLowerCase().includes('seen'))
        const llmSummary = email.llm_summary || undefined
        const labels = email.id ? labelsByEmailId[email.id] || [] : []

        return {
          id: email.id,
          uid: email.uid,
          subject,
          from,
          date,
          isUnread,
          llmSummary,
          labels,
        }
      })

      return mapped
        .sort((a: { labels?: unknown[]; isUnread: boolean; date: Date }, b: { labels?: unknown[]; isUnread: boolean; date: Date }) => {
          const aHasLabels = (a.labels?.length || 0) > 0
          const bHasLabels = (b.labels?.length || 0) > 0
          if (aHasLabels !== bHasLabels) {
            return aHasLabels ? -1 : 1
          }
          if (a.isUnread !== b.isUnread) {
            return a.isUnread ? -1 : 1
          }
          return b.date.getTime() - a.date.getTime()
        })
        .slice(0, 6)
    },
    {
      enabled: hasEmailAddress,
      staleTime: 2 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  )

  // Fetch students with pending file reviews
  const { data: pendingFileReviews, isLoading: pendingFileReviewsLoading } = useQuery(
    'dashboard/pending-file-reviews',
    async () => {
      try {
        const response = await api.get('/student-files/queue/students-overview');
        const students = response.data.data || [];
        
        // Load photos for all students
        const studentUnids = students.map((s: any) => s.sophomorixUnid);
        const photos = await loadStudentPhotos(studentUnids);
        
        // Add photos to student data
        const studentsWithPhotos = students.map((student: any) => ({
          ...student,
          photo: photos[student.sophomorixUnid] || null
        }));
        
        return {
          ...response.data,
          data: studentsWithPhotos
        };
      } catch (error: any) {
        // If user doesn't have queue access, return empty data
        if (error?.response?.status === 403) {
          return { data: [], total_pending: 0 };
        }
        console.error('Failed to load pending file reviews:', error);
        return { data: [], total_pending: 0 };
      }
    },
    {
      staleTime: 2 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  // Pending access requests (for Schulleiter, Abteilungsleiter, Klassenlehrer)
  const { data: pendingAccessRequests, isLoading: pendingAccessRequestsLoading } = useQuery(
    'dashboard/pending-access-requests',
    async () => {
      try {
        const response = await api.get('/student-files/access-requests/pending');
        return response.data;
      } catch (error: any) {
        if (error?.response?.status === 403) return { data: [] };
        console.error('Failed to load pending access requests:', error);
        return { data: [] };
      }
    },
    { staleTime: 2 * 60 * 1000, cacheTime: 10 * 60 * 1000, refetchOnWindowFocus: false }
  );

  const collectedRoles = new Set<string>()
  if (user?.role) {
    collectedRoles.add(user.role)
  }
  ;(user?.roles || []).forEach((role) => {
    if (role) collectedRoles.add(String(role))
  })
  ;(user?.allRoles || []).forEach((role) => {
    if (role) collectedRoles.add(String(role))
  })

  const isAdmin = Array.from(collectedRoles).some((role) => {
    const normalized = role.trim().toLowerCase()
    return normalized === 'admin' || normalized === 'administrator' || normalized === 'superadmin' || normalized === 'super_admin'
  }) || (user?.username || '').trim().toLowerCase() === 'admin'
  const isAdminOrSchulleitung =
    isAdmin ||
    Array.from(collectedRoles).some((role) => String(role || '').trim().toLowerCase() === 'schulleitung')

  const isStudentUser = (user?.role || '').trim().toLowerCase() === 'student'

  const widgetAvailability = useMemo<DashboardWidgetAvailability>(
    () => ({
      kiHub: isAdminOrSchulleitung,
      pendingFileReviews: true,
      pendingAccess: true,
      emails: hasEmailAddress,
      classRanking: true,
      appointments: true,
      timetable: !isStudentUser,
      kpiStats: true,
      activeSessions: true,
      recentBlocksAndPlans: true,
      nameScores: true,
      quickLinks: true,
    }),
    [hasEmailAddress, isAdminOrSchulleitung, isStudentUser]
  )

  const defaultWidgetOrder = useMemo(
    () => getDefaultWidgetOrder(widgetAvailability),
    [widgetAvailability]
  )
  const [widgetOrder, setWidgetOrder] = useState<DashboardWidgetId[]>(defaultWidgetOrder)

  useEffect(() => {
    const mergedOrder = mergeWidgetOrderWithDefaults(dashboardLayout?.widgetOrder, widgetAvailability)
    setWidgetOrder(mergedOrder)
  }, [dashboardLayout?.widgetOrder, widgetAvailability])

  const saveLayoutMutation = useMutation(
    async (nextWidgetOrder: DashboardWidgetId[]) => {
      await api.put('/profile/dashboard-layout', { widgetOrder: nextWidgetOrder })
      return nextWidgetOrder
    },
    {
      onSuccess: (nextWidgetOrder) => {
        queryClient.setQueryData('dashboard/widget-layout', { widgetOrder: nextWidgetOrder })
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Dashboard-Layout konnte nicht gespeichert werden.')
      },
    }
  )

  const refreshRankingMutation = useMutation<ClassRankingResponse, any>(
    async () => {
      const response = await api.post('/statistics/class-ranking/refresh', { force: true })
      return response.data as ClassRankingResponse
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData<ClassRankingResponse>('dashboard/class-ranking', {
          ...data,
          source: 'manual',
        })
        if (data.skipped) {
          toast.success('Keine Neuberechnung nötig – gespeichertes Ranking wurde geladen.')
          return
        }
        toast.success('Klassenranking aktualisiert')
      },
      onError: (error: any) => {
        const message =
          error?.response?.data?.error ||
          error?.message ||
          'Aktualisierung des Klassenrankings fehlgeschlagen'
        toast.error(message)
      },
    }
  )

  const handleManualRefresh = () => {
    if (!isAdmin) {
      toast.error('Nur Administratoren können das Klassenranking aktualisieren.')
      return
    }
    if (refreshRankingMutation.isLoading) {
      return
    }
    refreshRankingMutation.mutate()
  }

  const formatPercentage = (value: number) =>
    Number.isFinite(value)
      ? value.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
      : '0,0'

  const formatPercentageWithSuffix = (value: number | null | undefined) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return '–'
    }
    return `${formatPercentage(value)}%`
  }

  const formatTrendDelta = (value: number | null | undefined) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return '0,0%'
    }
    return `${formatPercentage(Math.abs(value))}%`
  }

  const formatBaseline = (value: number | null | undefined) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return '–'
    }
    return `${formatPercentage(value)}%`
  }

  const rankingEntries = classRanking?.data ?? []
  const rankingCalculatedAt = classRanking?.calculatedAt
    ? new Date(classRanking.calculatedAt).toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null
  const rankingValidUntil = classRanking?.validUntil
    ? new Date(classRanking.validUntil).toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null
  const rankingSourceLabel = classRanking?.source
    ? (
        {
          cache: 'Cache',
          history: 'Historie',
          fresh: 'Neu berechnet',
          manual: 'Manuell',
        } as Record<string, string>
      )[classRanking.source] || classRanking.source
    : null

  const isRankingUpdating = refreshRankingMutation.isLoading

  const [showAllClasses, setShowAllClasses] = useState(false)

  useEffect(() => {
    setShowAllClasses(false)
  }, [classRanking?.calculatedAt, rankingEntries.length])

  const visibleEntriesCount = showAllClasses ? rankingEntries.length : Math.min(3, rankingEntries.length)
  const visibleRankingEntries = rankingEntries.slice(0, visibleEntriesCount)
  const hasMoreClasses = rankingEntries.length > 3
  const remainingCollapsedCount = Math.max(rankingEntries.length - 3, 0)
  const limitedBuildingBlocks = (recentBuildingBlocks ?? []).slice(0, 5)
  const limitedLearningPlans = (recentLearningPlans ?? []).slice(0, 5)
  const limitedSnakeHighscores = (snakeHighscores ?? []).slice(0, 5)
  const limitedQuizHighscores = (quizHighscores ?? []).slice(0, 5)
  const addableWidgets = useMemo(
    () => listAddableWidgets(widgetOrder, widgetAvailability),
    [widgetOrder, widgetAvailability]
  )

  const persistWidgetOrder = (nextWidgetOrder: DashboardWidgetId[]) => {
    setWidgetOrder(nextWidgetOrder)
    saveLayoutMutation.mutate(nextWidgetOrder)
  }

  const handleWidgetReorder = (nextIds: string[]) => {
    const nextWidgetOrder = nextIds as DashboardWidgetId[]
    persistWidgetOrder(nextWidgetOrder)
  }

  const handleWidgetRemove = (widgetId: DashboardWidgetId) => {
    const nextWidgetOrder = widgetOrder.filter((id) => id !== widgetId)
    persistWidgetOrder(nextWidgetOrder)
  }

  const handleWidgetAdd = (widgetId: DashboardWidgetId) => {
    if (widgetOrder.includes(widgetId)) return
    const nextWidgetOrder = [...widgetOrder, widgetId]
    persistWidgetOrder(nextWidgetOrder)
  }

  const handleWidgetReset = () => {
    persistWidgetOrder(defaultWidgetOrder)
  }
  const widgetOrderIndex = useMemo(
    () => new Map(widgetOrder.map((widgetId, index) => [widgetId, index])),
    [widgetOrder]
  )

  const hasWidget = (widgetId: DashboardWidgetId) => widgetOrderIndex.has(widgetId)

  const getWidgetContainerStyle = (
    widgetId: DashboardWidgetId,
    style: CSSProperties
  ): CSSProperties => ({
    ...style,
    order: widgetOrderIndex.get(widgetId) ?? 999,
  })

  const renderWidgetToolbar = (
    widgetId: DashboardWidgetId,
    dragHandleProps: Record<string, unknown>
  ) => (
    <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
      <div className="text-sm font-medium text-gray-700 dark:text-gray-200">{DASHBOARD_WIDGET_TITLE_BY_ID[widgetId]}</div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          aria-label={`Widget ${DASHBOARD_WIDGET_TITLE_BY_ID[widgetId]} verschieben`}
          {...(dragHandleProps as ButtonHTMLAttributes<HTMLButtonElement>)}
        >
          <GripVertical className="h-4 w-4" aria-hidden />
        </Button>
        <Button
          size="sm"
          variant="outline"
          aria-label={`Widget ${DASHBOARD_WIDGET_TITLE_BY_ID[widgetId]} entfernen`}
          onClick={() => handleWidgetRemove(widgetId)}
        >
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    </div>
  )

  if (isLoading) {
    return (
      <div className="w-full animate-pulse space-y-6">
        <div className="h-10 max-w-md rounded-lg bg-gray-200 dark:bg-gray-800" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
          {[...Array(6)].map((_, i) => (
            <Tile key={i} variant="compact">
              <TileContent className="py-4">
                <div className="mx-auto h-8 w-8 rounded-md bg-gray-200 dark:bg-gray-700" />
                <div className="mx-auto mt-3 h-4 w-16 rounded bg-gray-200 dark:bg-gray-700" />
                <div className="mx-auto mt-2 h-6 w-10 rounded bg-gray-200 dark:bg-gray-700" />
              </TileContent>
            </Tile>
          ))}
        </div>
      </div>
    )
  }

  const statCards: {
    name: string
    value: number
    icon: typeof Users
    iconWrap: string
    iconClass: string
  }[] = [
    {
      name: 'Schüler',
      value: stats?.totalStudents || 0,
      icon: Users,
      iconWrap: 'bg-primary-100 dark:bg-primary-900/40',
      iconClass: 'text-primary-600 dark:text-primary-300',
    },
    {
      name: 'Aktive Sitzungen',
      value: stats?.activeSessions || 0,
      icon: Calendar,
      iconWrap: 'bg-emerald-100 dark:bg-emerald-900/40',
      iconClass: 'text-emerald-600 dark:text-emerald-300',
    },
    {
      name: 'Bausteine',
      value: stats?.buildingBlocks || 0,
      icon: BookOpen,
      iconWrap: 'bg-violet-100 dark:bg-violet-900/40',
      iconClass: 'text-violet-600 dark:text-violet-300',
    },
    {
      name: 'Lernpläne',
      value: stats?.learningPlans || 0,
      icon: FileText,
      iconWrap: 'bg-amber-100 dark:bg-amber-900/40',
      iconClass: 'text-amber-700 dark:text-amber-300',
    },
    {
      name: 'Aufgaben',
      value: stats?.totalTasks || 0,
      icon: CheckSquare,
      iconWrap: 'bg-indigo-100 dark:bg-indigo-900/40',
      iconClass: 'text-indigo-600 dark:text-indigo-300',
    },
    {
      name: 'Belege',
      value: stats?.totalEvidence || 0,
      icon: FileCheck,
      iconWrap: 'bg-teal-100 dark:bg-teal-900/40',
      iconClass: 'text-teal-600 dark:text-teal-300',
    },
  ]
  const pendingFileReviewItems = pendingFileReviews?.data || []
  const pendingAccessItems = pendingAccessRequests?.data || []

  return (
    <div className="w-full max-w-none space-y-8">
      <ListToolbar
        title="Dashboard"
        description="Willkommen bei Lernrakete — Übersicht über Ihre Daten (Schriftgröße und Farbschema wie unter Darstellung in /Showcase)."
        trailing={
          <Button size="sm" onClick={() => setIsWidgetCatalogOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            Widget hinzufügen
          </Button>
        }
      />

      <SortableVerticalList itemIds={widgetOrder} onReorder={handleWidgetReorder} containerClassName="flex flex-col gap-8">
      {hasWidget('kiHub') && (
        <SortableListItem id="kiHub">
          {({ setNodeRef, style, dragHandleProps }) => (
            <div ref={setNodeRef} style={getWidgetContainerStyle('kiHub', style)} className="space-y-3">
              {renderWidgetToolbar('kiHub', dragHandleProps)}
              <KiHubUsageCard title="Monatsübersicht Assistenzsysteme" />
            </div>
          )}
        </SortableListItem>
      )}

      {/* Pending File Reviews Widget */}
      {hasWidget('pendingFileReviews') && (
        <SortableListItem id="pendingFileReviews">
          {({ setNodeRef, style, dragHandleProps }) => (
            <div ref={setNodeRef} style={getWidgetContainerStyle('pendingFileReviews', style)} className="space-y-3">
              {renderWidgetToolbar('pendingFileReviews', dragHandleProps)}
        <Tile>
          <TileHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 flex-wrap items-center gap-3">
                <FolderOpen className="h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden />
                <TileTitle as="h2" className="text-base">
                  Aktenprüfung ausstehend
                </TileTitle>
                <Badge variant={(pendingFileReviews?.total_pending || 0) > 0 ? 'danger' : 'neutral'}>
                  {pendingFileReviews?.total_pending || 0} Datei{(pendingFileReviews?.total_pending || 0) !== 1 ? 'en' : ''}
                </Badge>
              </div>
              <Link to="/Akten-Prüfung" className={linkOutlineSmClass}>
                Übersicht öffnen
                <FolderOpen className="h-4 w-4" aria-hidden />
              </Link>
            </div>
            <TileDescription className="mt-2">
              {pendingFileReviewItems.length} Schüler
              {pendingFileReviewItems.length !== 1 ? ' haben' : ' hat'} ausstehende Dateien zur Prüfung.
            </TileDescription>
          </TileHeader>
          <TileContent>
            {pendingFileReviewsLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, index) => (
                  <div
                    key={index}
                    className="h-16 rounded-lg bg-gray-100 animate-pulse dark:bg-gray-800"
                  />
                ))}
              </div>
            ) : pendingFileReviewItems.length > 0 ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {pendingFileReviewItems.slice(0, 6).map((student: any) => {
                  const daysWaiting = Math.floor(
                    (Date.now() - new Date(student.oldest_submission).getTime()) / (1000 * 60 * 60 * 24)
                  )
                  const urgency =
                    daysWaiting >= 7 ? 'danger' : daysWaiting >= 3 ? 'warning' : ('info' as const)
                  const dotClass =
                    urgency === 'danger'
                      ? 'bg-red-500'
                      : urgency === 'warning'
                        ? 'bg-amber-500'
                        : 'bg-primary-500'

                  return (
                    <Link
                      key={student.sophomorixUnid}
                      to={`/Akten?student=${student.sophomorixUnid}#official-files`}
                      className="block no-underline"
                    >
                      <Tile className="h-full transition-shadow hover:shadow-md">
                        <TileContent className="py-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex min-w-0 flex-1 items-start gap-2">
                              <div className="group relative shrink-0">
                                {student.photo ? (
                                  <img
                                    src={createPhotoDataUrl(student.photo)}
                                    alt={`${student.student_first_name} ${student.student_name}`}
                                    className="h-[3.75rem] w-[3.75rem] rounded-full border-2 border-gray-200 object-cover transition-transform duration-200 hover:z-50 hover:scale-[2] hover:shadow-lg dark:border-gray-600"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement
                                      target.style.display = 'none'
                                      const parent = target.parentElement
                                      if (parent) {
                                        const fallback = document.createElement('div')
                                        fallback.className =
                                          'flex h-[3.75rem] w-[3.75rem] items-center justify-center rounded-full border-2 border-gray-200 bg-primary-100 text-sm font-semibold text-primary-700 transition-transform duration-200 hover:z-50 hover:scale-[2] hover:shadow-lg dark:border-gray-600 dark:bg-primary-900/40 dark:text-primary-200'
                                        fallback.textContent =
                                          `${student.student_first_name?.[0] || ''}${student.student_name?.[0] || ''}`.toUpperCase()
                                        parent.appendChild(fallback)
                                      }
                                    }}
                                  />
                                ) : (
                                  <div className="flex h-[3.75rem] w-[3.75rem] items-center justify-center rounded-full border-2 border-gray-200 bg-primary-100 text-sm font-semibold text-primary-700 transition-transform duration-200 hover:z-50 hover:scale-[2] hover:shadow-lg dark:border-gray-600 dark:bg-primary-900/40 dark:text-primary-200">
                                    {`${student.student_first_name?.[0] || ''}${student.student_name?.[0] || ''}`.toUpperCase()}
                                  </div>
                                )}
                              </div>
                              <div className={clsx('mt-2 h-2 w-2 shrink-0 rounded-full', dotClass)} aria-hidden />
                              <div className="min-w-0 flex-1 space-y-1.5">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-50">
                                    {student.student_first_name} {student.student_name}
                                  </p>
                                  <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                                    ({student.student_class})
                                  </span>
                                </div>
                                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                                  <span className="flex items-center gap-1">
                                    <FileText className="h-3 w-3 shrink-0" aria-hidden />
                                    {student.pending_count} Datei{student.pending_count !== 1 ? 'en' : ''}
                                  </span>
                                  <span
                                    className={clsx(
                                      'flex items-center gap-1',
                                      urgency === 'danger' && 'text-red-600 dark:text-red-400',
                                      urgency === 'warning' && 'text-amber-600 dark:text-amber-400',
                                      urgency === 'info' && 'text-primary-600 dark:text-primary-400'
                                    )}
                                  >
                                    <Clock className="h-3 w-3 shrink-0" aria-hidden />
                                    {daysWaiting === 0
                                      ? 'Heute'
                                      : daysWaiting === 1
                                        ? 'Gestern'
                                        : `vor ${daysWaiting} Tagen`}
                                  </span>
                                </div>
                                {student.labels && student.labels.length > 0 && (
                                  <ChipList className="pt-1">
                                    {student.labels.slice(0, 3).map((label: any) =>
                                      label.color ? (
                                        <Chip
                                          key={label.id}
                                          size="sm"
                                          variant="outline"
                                          className="border-transparent"
                                          style={{
                                            backgroundColor: `${label.color}26`,
                                            color: label.color,
                                            borderColor: label.color,
                                          }}
                                        >
                                          {label.name}
                                        </Chip>
                                      ) : (
                                        <Chip key={label.id} size="sm" variant="neutral">
                                          {label.name}
                                        </Chip>
                                      )
                                    )}
                                    {student.labels.length > 3 && (
                                      <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                        +{student.labels.length - 3}
                                      </span>
                                    )}
                                  </ChipList>
                                )}
                                {student.categories && (
                                  <p className="truncate text-xs text-gray-400 dark:text-gray-500">
                                    {student.categories}
                                  </p>
                                )}
                              </div>
                            </div>
                            <span className="shrink-0 text-xs text-gray-400 dark:text-gray-500">→</span>
                          </div>
                        </TileContent>
                      </Tile>
                    </Link>
                  )
                })}
                {pendingFileReviewItems.length > 5 && (
                  <div className="col-span-full text-center pt-2">
                    <Link
                      to="/Akten-Prüfung"
                      className="text-sm font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                      {pendingFileReviewItems.length - 5} weitere Schüler anzeigen →
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <EmptyState
                title="Keine ausstehenden Prüfungen"
                description="Es liegen keine Akten zur Prüfung vor."
              />
            )}
          </TileContent>
        </Tile>
            </div>
          )}
        </SortableListItem>
      )}

      {/* Pending Access Requests Widget */}
      {hasWidget('pendingAccess') && (
        <SortableListItem id="pendingAccess">
          {({ setNodeRef, style, dragHandleProps }) => (
            <div ref={setNodeRef} style={getWidgetContainerStyle('pendingAccess', style)} className="space-y-3">
              {renderWidgetToolbar('pendingAccess', dragHandleProps)}
        <Tile>
          <TileHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 flex-wrap items-center gap-3">
                <ShieldQuestion className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                <TileTitle as="h2" className="text-base">
                  Zugriffsanfragen auf offizielle Akten
                </TileTitle>
                <Badge variant={pendingAccessItems.length > 0 ? 'warning' : 'neutral'}>
                  {pendingAccessItems.length} Anfrage{pendingAccessItems.length !== 1 ? 'n' : ''}
                </Badge>
              </div>
              <Link to="/Akten-Prüfung" className={linkOutlineSmClass}>
                Übersicht öffnen
                <ShieldQuestion className="h-4 w-4" aria-hidden />
              </Link>
            </div>
            <TileDescription className="mt-2">
              Kolleg:innen haben Zugriff auf offizielle Schülerakten beantragt. Bitte prüfen und befristet gewähren oder
              ablehnen.
            </TileDescription>
          </TileHeader>
          <TileContent>
            {pendingAccessRequestsLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-16 rounded-lg bg-gray-100 animate-pulse dark:bg-gray-800" />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {pendingAccessItems.length > 0 ? pendingAccessItems.slice(0, 5).map((req: any) => (
                  <Link
                    key={req.id}
                    to={`/Akten?student=${req.sophomorix_unid}#access-requests`}
                    className="block no-underline"
                  >
                    <Tile className="transition-shadow hover:shadow-md">
                      <TileContent className="py-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                              {req.student_first} {req.student_last} ({req.student_class})
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Beantragt von{' '}
                              {req.req_first || req.req_last
                                ? `${req.req_first || ''} ${req.req_last || ''}`.trim()
                                : req.requested_by_username}
                              {' · '}
                              {format(parseISO(req.requested_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                            </p>
                            {req.request_comment && (
                              <p className="mt-1 line-clamp-1 text-xs italic text-gray-600 dark:text-gray-300">
                                {req.request_comment}
                              </p>
                            )}
                          </div>
                          <span className="shrink-0 text-sm font-medium text-amber-600 dark:text-amber-400">→</span>
                        </div>
                      </TileContent>
                    </Tile>
                  </Link>
                )) : (
                  <EmptyState title="Keine offenen Anfragen" description="Derzeit liegen keine neuen Zugriffsanfragen vor." />
                )}
                {pendingAccessItems.length > 5 && (
                  <div className="text-center pt-2">
                    <Link
                      to="/Akten-Prüfung"
                      className="text-sm font-medium text-amber-700 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300"
                    >
                      {pendingAccessItems.length - 5} weitere Anfragen →
                    </Link>
                  </div>
                )}
              </div>
            )}
          </TileContent>
        </Tile>
            </div>
          )}
        </SortableListItem>
      )}

      {hasWidget('emails') && (
        <SortableListItem id="emails">
          {({ setNodeRef, style, dragHandleProps }) => (
            <div ref={setNodeRef} style={getWidgetContainerStyle('emails', style)} className="space-y-3">
              {renderWidgetToolbar('emails', dragHandleProps)}
        <Tile>
          <TileHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 shrink-0 text-indigo-600 dark:text-indigo-400" aria-hidden />
                <TileTitle as="h2" className="text-base">
                  Wichtige E-Mails
                </TileTitle>
              </div>
              <Link to="/E-Mail" className={linkOutlineSmClass}>
                Öffnen
                <Mail className="h-4 w-4" aria-hidden />
              </Link>
            </div>
            <TileDescription className="mt-2">Übersicht der 6 wichtigsten E-Mails aus Ihrem Posteingang.</TileDescription>
          </TileHeader>
          <TileContent>
            {importantEmailsLoading ? (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {[...Array(6)].map((_, index) => (
                  <div key={index} className="h-20 rounded-lg bg-gray-100 animate-pulse dark:bg-gray-800" />
                ))}
              </div>
            ) : importantEmails && importantEmails.length > 0 ? (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {importantEmails.map((email, index) => (
                  <Link key={email.id ?? email.uid ?? index} to="/E-Mail" className="block no-underline">
                    <Tile className="h-full transition-shadow hover:shadow-md">
                      <TileContent className="py-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex min-w-0 flex-1 items-start gap-3">
                            <span
                              className={clsx(
                                'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                                email.isUnread ? 'bg-indigo-500' : 'bg-gray-300 dark:bg-gray-600'
                              )}
                              aria-hidden
                            />
                            <div className="min-w-0 space-y-1">
                              <p className="line-clamp-1 text-sm font-semibold text-gray-900 dark:text-gray-50">
                                {decodeRFC2047(email.subject)}
                              </p>
                              <p className="line-clamp-1 text-xs text-gray-500 dark:text-gray-400">
                                {decodeRFC2047(email.from)}
                              </p>
                              {email.llmSummary ? (
                                <p className="line-clamp-2 text-xs italic text-gray-600 dark:text-gray-300">
                                  {email.llmSummary}
                                </p>
                              ) : (
                                <p className="text-xs italic text-gray-400 dark:text-gray-500">Keine Kurz-Zusammenfassung</p>
                              )}
                              {email.labels && email.labels.length > 0 ? (
                                <ChipList className="pt-1">
                                  {email.labels.map((label) =>
                                    label.color ? (
                                      <Chip
                                        key={label.id}
                                        size="sm"
                                        variant="outline"
                                        className="border-transparent text-white"
                                        style={{ backgroundColor: label.color, borderColor: label.color }}
                                      >
                                        {label.name}
                                      </Chip>
                                    ) : (
                                      <Chip key={label.id} size="sm" variant="primary">
                                        {label.name}
                                      </Chip>
                                    )
                                  )}
                                </ChipList>
                              ) : (
                                <p className="pt-1 text-[11px] text-gray-400 dark:text-gray-500">Keine Labels</p>
                              )}
                            </div>
                          </div>
                          <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                            {format(email.date, 'dd.MM.yyyy HH:mm', { locale: de })}
                          </span>
                        </div>
                      </TileContent>
                    </Tile>
                  </Link>
                ))}
              </div>
            ) : (
              <EmptyState title="Keine E-Mails im Posteingang" />
            )}
          </TileContent>
        </Tile>
            </div>
          )}
        </SortableListItem>
      )}

      {hasWidget('classRanking') && (
        <SortableListItem id="classRanking">
          {({ setNodeRef, style, dragHandleProps }) => (
            <div ref={setNodeRef} style={getWidgetContainerStyle('classRanking', style)} className="space-y-3">
              {renderWidgetToolbar('classRanking', dragHandleProps)}
      <Tile className="overflow-hidden">
        <div className="border-b border-primary-700/30 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 px-6 py-5 text-white dark:from-primary-950 dark:via-primary-900 dark:to-primary-950">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="flex items-center gap-2 text-lg font-semibold">
                <Crown className="h-6 w-6 shrink-0" aria-hidden />
                Klassenranking
              </div>
              <p className="mt-1 text-sm text-primary-100 dark:text-primary-200/90">
                {rankingCalculatedAt
                  ? `Stand: ${rankingCalculatedAt}${rankingValidUntil ? ` • nächstes Update ca. ${rankingValidUntil} Uhr` : ''}`
                  : 'Berechne Durchschnittswerte...'}
              </p>
              {rankingSourceLabel && (
                <p className="mt-1 text-xs text-primary-100/80 dark:text-primary-300/80">Quelle: {rankingSourceLabel}</p>
              )}
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleManualRefresh}
              disabled={!isAdmin || isClassRankingLoading || isRankingUpdating}
              title={isAdmin ? undefined : 'Nur Administratoren können aktualisieren'}
              className={clsx(
                'border-white/40 bg-white/10 text-white hover:bg-white/20 dark:border-white/20 dark:bg-white/5 dark:hover:bg-white/10',
                !isAdmin && 'opacity-60'
              )}
            >
              <RefreshCw
                className={clsx(
                  'h-4 w-4',
                  (isClassRankingLoading || isRankingUpdating) && 'animate-spin'
                )}
                aria-hidden
              />
              Aktualisieren
            </Button>
          </div>
        </div>
        <TileContent className="bg-gray-50 py-6 dark:bg-gray-950/50">
          {isClassRankingLoading ? (
            <div className="grid gap-4 md:grid-cols-2">
              {[...Array(4)].map((_, index) => (
                <div
                  key={index}
                  className="h-24 animate-pulse rounded-xl border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800"
                />
              ))}
            </div>
          ) : visibleRankingEntries.length > 0 ? (
            <>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {visibleRankingEntries.map((entry, index) => {
                  const rankIndex = rankingEntries.findIndex((item) => item.className === entry.className)
                  const highlight = rankIndex === 0
                  const topTier = rankIndex >= 0 && rankIndex < 3
                  const fallbackIndex = rankIndex >= 0 ? rankIndex : index
                  const displayRank = fallbackIndex + 1
                  const trendDelta = entry.trendDelta ?? 0
                  const trendBaselineValue = entry.trendBaseline ?? null
                  const isPositiveTrend = trendDelta > 0
                  const isNegativeTrend = trendDelta < 0
                  const trendVariant: 'success' | 'danger' | 'neutral' = isPositiveTrend
                    ? 'success'
                    : isNegativeTrend
                      ? 'danger'
                      : 'neutral'
                  const trendIcon = isPositiveTrend ? '▲' : isNegativeTrend ? '▼' : '▬'
                  const topStudents = (entry.topStudents || [])
                    .filter((student) => typeof student.progress === 'number' && student.progress > 0)
                    .slice(0, 3)

                  return (
                    <Tile
                      key={entry.className}
                      className={clsx(
                        highlight &&
                          'border-primary-200 bg-gradient-to-r from-primary-50 via-indigo-50 to-violet-50 shadow-md dark:border-primary-800 dark:from-primary-950/60 dark:via-indigo-950/40 dark:to-violet-950/40',
                        !highlight &&
                          topTier &&
                          'border-violet-200 bg-violet-50/60 dark:border-violet-800 dark:bg-violet-950/30',
                        !highlight && !topTier && 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900'
                      )}
                    >
                      <TileContent className="py-5">
                        <div className="flex items-start gap-4">
                          <div
                            className={clsx(
                              'flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-lg font-bold shadow-sm',
                              highlight &&
                                'bg-white text-primary-600 dark:bg-gray-800 dark:text-primary-300 dark:shadow-none',
                              !highlight &&
                                topTier &&
                                'bg-white text-violet-600 dark:bg-gray-800 dark:text-violet-300 dark:shadow-none',
                              !highlight && !topTier && 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100'
                            )}
                          >
                            {displayRank || '–'}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-start justify-between gap-4">
                              <div>
                                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                  Klasse
                                </p>
                                <p className="text-xl font-semibold text-gray-900 dark:text-gray-50">{entry.className}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                  Ø Gesamtleistung
                                </p>
                                <p
                                  className={clsx(
                                    'text-2xl font-bold tabular-nums',
                                    highlight ? 'text-primary-600 dark:text-primary-400' : 'text-gray-900 dark:text-gray-50'
                                  )}
                                >
                                  {formatPercentage(entry.averageProgress)}%
                                </p>
                              </div>
                            </div>
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              <Badge variant={trendVariant}>
                                {trendIcon} {formatTrendDelta(trendDelta)}
                                {trendBaselineValue !== null ? ` (Ø ${formatBaseline(trendBaselineValue)})` : ''}
                              </Badge>
                              {isPositiveTrend && trendDelta >= 5 && (
                                <Badge variant="success" className="bg-primary-600 text-white dark:bg-primary-500">
                                  Aufsteiger
                                </Badge>
                              )}
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-3 text-sm text-gray-600 dark:text-gray-300">
                              <div>
                                <span className="font-medium text-gray-900 dark:text-gray-100">Basis:</span>{' '}
                                {formatPercentage(entry.averageBaseProgress)}%
                              </div>
                              <div>
                                <span className="font-medium text-gray-900 dark:text-gray-100">Bonus:</span>{' '}
                                {formatPercentage(entry.averageBonusProgress)}%
                              </div>
                              <div>
                                <span className="font-medium text-gray-900 dark:text-gray-100">Schüler:</span>{' '}
                                {entry.studentCount}
                              </div>
                              <div>
                                <span className="font-medium text-gray-900 dark:text-gray-100">LP Ø:</span>{' '}
                                {formatPercentage(entry.averageLearningPlans)}
                              </div>
                              <div>
                                <span className="font-medium text-gray-900 dark:text-gray-100">Reg.-Quote:</span>{' '}
                                {formatPercentageWithSuffix(entry.regularTaskCompletionRate)}
                              </div>
                              <div>
                                <span className="font-medium text-gray-900 dark:text-gray-100">Bonus-Quote:</span>{' '}
                                {formatPercentageWithSuffix(entry.bonusTaskCompletionRate)}
                              </div>
                            </div>
                            {topStudents.length > 0 && (
                              <div className="mt-4 rounded-lg border border-gray-100 bg-white/90 p-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900/80 dark:text-gray-200">
                                <p className="font-medium text-gray-900 dark:text-gray-50">Top 3 Schüler</p>
                                <ul className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-300">
                                  {topStudents.map((student, studentIndex) => (
                                    <li
                                      key={student.id || `${entry.className}-${studentIndex}`}
                                      className="flex items-center justify-between gap-2"
                                    >
                                      <span className="min-w-0 truncate">
                                        {studentIndex + 1}. {student.name}
                                      </span>
                                      <span className="shrink-0 font-semibold text-primary-600 dark:text-primary-400">
                                        {formatPercentage(student.progress)}%
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </TileContent>
                    </Tile>
                  )
                })}
              </div>
              {hasMoreClasses && (
                <div className="mt-6 flex justify-center">
                  <Button type="button" size="sm" variant="outline" onClick={() => setShowAllClasses((prev) => !prev)}>
                    {showAllClasses ? 'Weniger anzeigen' : `Mehr anzeigen (${remainingCollapsedCount} weitere)`}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <EmptyState
              title="Keine Daten für das Klassenranking verfügbar."
              className="py-6"
            />
          )}
        </TileContent>
      </Tile>
            </div>
          )}
        </SortableListItem>
      )}

      {hasWidget('appointments') && (
        <SortableListItem id="appointments">
          {({ setNodeRef, style, dragHandleProps }) => (
            <div ref={setNodeRef} style={getWidgetContainerStyle('appointments', style)} className="space-y-3">
              {renderWidgetToolbar('appointments', dragHandleProps)}
      <Tile>
        <TileHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <CalendarDays className="h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden />
              <TileTitle as="h2" className="text-base">
                Terminbuchungen
              </TileTitle>
            </div>
            <Link to="/appointments" className={linkOutlineSmClass}>
              Öffnen
              <CalendarDays className="h-4 w-4" aria-hidden />
            </Link>
          </div>
          <TileDescription className="mt-2">
            Verwalten Sie Ihre Terminbuchungen für Elternsprechtage und Sprechstunden.
          </TileDescription>
        </TileHeader>

        {upcomingAppointments && upcomingAppointments.length > 0 && (
          <>
            <TileFooter className="border-b-0 bg-gray-50 dark:bg-gray-950/50">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  <Calendar className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
                  <span className="text-base font-semibold text-gray-900 dark:text-gray-50">Nächste Termine</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">({upcomingAppointments.length})</span>
                </div>
                <Link
                  to="/appointments"
                  className="text-sm font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
                >
                  Alle anzeigen →
                </Link>
              </div>
            </TileFooter>
            <TileContent>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {upcomingAppointments.map((appointment: any) => (
                  <Link key={appointment.booking_id} to="/appointments" className="block no-underline">
                    <Tile className="h-full transition-shadow hover:shadow-md">
                      <TileContent className="py-4">
                        <div className="mb-2 flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                            <Clock className="h-4 w-4 shrink-0" aria-hidden />
                            <span className="font-medium">
                              {format(parseISO(appointment.start_time), 'dd.MM.yyyy', { locale: de })}
                            </span>
                          </div>
                          <Badge
                            variant={
                              appointment.booking_status === 'pending_confirmation' ? 'warning' : 'success'
                            }
                          >
                            {appointment.booking_status === 'pending_confirmation'
                              ? 'Bestätigung ausstehend'
                              : 'Gebucht'}
                          </Badge>
                        </div>
                        <div className="mb-1 text-sm font-semibold text-gray-900 dark:text-gray-50">
                          {format(parseISO(appointment.start_time), 'HH:mm', { locale: de })} -{' '}
                          {format(parseISO(appointment.end_time), 'HH:mm', { locale: de })}
                        </div>
                        {appointment.parent_name && (
                          <div className="mt-2 flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300">
                            <User className="h-3 w-3 shrink-0" aria-hidden />
                            <span>{appointment.parent_name}</span>
                          </div>
                        )}
                        {appointment.student_name && (
                          <div className="ml-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Schüler: {appointment.student_name}
                          </div>
                        )}
                        {appointment.config_title && (
                          <div className="mt-2 border-t border-gray-100 pt-2 text-xs text-gray-400 dark:border-gray-700 dark:text-gray-500">
                            {appointment.config_title}
                          </div>
                        )}
                      </TileContent>
                    </Tile>
                  </Link>
                ))}
              </div>
            </TileContent>
          </>
        )}
      </Tile>
            </div>
          )}
        </SortableListItem>
      )}

      {hasWidget('timetable') && (
        <SortableListItem id="timetable">
          {({ setNodeRef, style, dragHandleProps }) => (
            <div ref={setNodeRef} style={getWidgetContainerStyle('timetable', style)} className="space-y-3">
              {renderWidgetToolbar('timetable', dragHandleProps)}
              <DashboardTimetableWidget
                allowUserPicker={isAdminOrSchulleitung}
                authUserId={user?.id ?? null}
              />
            </div>
          )}
        </SortableListItem>
      )}

      {hasWidget('kpiStats') && (
        <SortableListItem id="kpiStats">
          {({ setNodeRef, style, dragHandleProps }) => (
            <div ref={setNodeRef} style={getWidgetContainerStyle('kpiStats', style)} className="space-y-3">
              {renderWidgetToolbar('kpiStats', dragHandleProps)}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        {statCards.map((stat) => {
          const Icon = stat.icon
          return (
            <Tile key={stat.name} variant="compact">
              <TileContent className="py-4">
                <div className="flex items-center gap-3">
                  <div
                    className={clsx(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-md',
                      stat.iconWrap
                    )}
                  >
                    <Icon className={clsx('h-5 w-5', stat.iconClass)} aria-hidden />
                  </div>
                  <dl className="min-w-0 flex-1">
                    <dt className="truncate text-xs font-medium text-gray-500 dark:text-gray-400">{stat.name}</dt>
                    <dd className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-50">{stat.value}</dd>
                  </dl>
                </div>
              </TileContent>
            </Tile>
          )
        })}
      </div>
            </div>
          )}
        </SortableListItem>
      )}

      {hasWidget('activeSessions') && (
        <SortableListItem id="activeSessions">
          {({ setNodeRef, style, dragHandleProps }) => (
            <div ref={setNodeRef} style={getWidgetContainerStyle('activeSessions', style)} className="space-y-3">
              {renderWidgetToolbar('activeSessions', dragHandleProps)}
      <Tile>
        <TileHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
              <TileTitle as="h2" className="text-base">
                Aktive Sitzungen heute
              </TileTitle>
            </div>
            <Link
              to="/lernbuero"
              className="text-sm font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
            >
              Alle anzeigen →
            </Link>
          </div>
        </TileHeader>
        <TileContent>
          <div className="space-y-3">
            {activeSessions && activeSessions.length > 0 ? (
              activeSessions.map((session) => (
                <Tile key={session.SessionID} variant="compact" className="shadow-none">
                  <TileContent className="flex items-center justify-between gap-3 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="h-2 w-2 shrink-0 rounded-full bg-emerald-400" aria-hidden />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-50">{session.Fach}</p>
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">{session.Lehrer}</p>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-sm text-gray-900 dark:text-gray-50">
                        {new Date(session.TimeStamp).toLocaleTimeString('de-DE', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{session.studentCount} Schüler</p>
                    </div>
                  </TileContent>
                </Tile>
              ))
            ) : (
              <EmptyState
                title="Keine aktiven Sitzungen heute"
                description="Sobald eine Sitzung läuft, erscheint sie hier."
              />
            )}
          </div>
        </TileContent>
      </Tile>
            </div>
          )}
        </SortableListItem>
      )}

      {hasWidget('recentBlocksAndPlans') && (
        <SortableListItem id="recentBlocksAndPlans">
          {({ setNodeRef, style, dragHandleProps }) => (
            <div ref={setNodeRef} style={getWidgetContainerStyle('recentBlocksAndPlans', style)} className="space-y-3">
              {renderWidgetToolbar('recentBlocksAndPlans', dragHandleProps)}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Tile>
          <TileHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 shrink-0 text-violet-600 dark:text-violet-400" aria-hidden />
                <TileTitle as="h2" className="text-base">
                  Neueste Bausteine
                </TileTitle>
              </div>
              <Link
                to="/building-blocks"
                className="text-sm font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Alle anzeigen →
              </Link>
            </div>
          </TileHeader>
          <TileContent>
            <div className="space-y-3">
              {limitedBuildingBlocks.length > 0 ? (
                limitedBuildingBlocks.map((block) => (
                  <Tile key={block.BID} variant="compact" className="shadow-none">
                    <TileContent className="py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-50">{block.NAME}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {block.FACH} • {block.STUFE} • {block.JAHR}
                          </p>
                        </div>
                        <p className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                          {new Date(block.START_DATUM).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                    </TileContent>
                  </Tile>
                ))
              ) : (
                <EmptyState title="Keine Bausteine verfügbar" />
              )}
            </div>
          </TileContent>
        </Tile>

        <Tile>
          <TileHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                <TileTitle as="h2" className="text-base">
                  Neueste Lernpläne
                </TileTitle>
              </div>
              <Link
                to="/learning-plans"
                className="text-sm font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Alle anzeigen →
              </Link>
            </div>
          </TileHeader>
          <TileContent>
            <div className="space-y-3">
              {limitedLearningPlans.length > 0 ? (
                limitedLearningPlans.map((plan) => (
                  <Tile key={plan.LPID} variant="compact" className="shadow-none">
                    <TileContent className="py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-50">{plan.NAME}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {plan.FACH} • {plan.STUFE} • {plan.JAHR}
                          </p>
                          <div className="mt-1">
                            <Badge variant={plan.STATUS === 'aktiv' ? 'success' : 'neutral'}>{plan.STATUS}</Badge>
                          </div>
                        </div>
                        <p className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                          {new Date(plan.START_DATUM).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                    </TileContent>
                  </Tile>
                ))
              ) : (
                <EmptyState title="Keine Lernpläne verfügbar" />
              )}
            </div>
          </TileContent>
        </Tile>
      </div>
            </div>
          )}
        </SortableListItem>
      )}

      {hasWidget('nameScores') && (
        <SortableListItem id="nameScores">
          {({ setNodeRef, style, dragHandleProps }) => (
            <div ref={setNodeRef} style={getWidgetContainerStyle('nameScores', style)} className="space-y-3">
              {renderWidgetToolbar('nameScores', dragHandleProps)}
      <Tile>
        <TileHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Trophy className="h-5 w-5 shrink-0 text-amber-500" aria-hidden />
              <TileTitle as="h2" className="text-base">
                Namen-lernen Highscores
              </TileTitle>
            </div>
            <Link
              to="/namen-lernen"
              className={clsx(linkOutlineSmClass, 'border-amber-200 text-amber-800 hover:bg-amber-50 dark:border-amber-800 dark:text-amber-200 dark:hover:bg-amber-950/40')}
            >
              <Gamepad2 className="h-4 w-4" aria-hidden />
              Spielen
            </Link>
          </div>
        </TileHeader>
        <TileContent>
          {limitedQuizHighscores.length > 0 || limitedSnakeHighscores.length > 0 ? (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <Tile className="bg-gray-50 dark:bg-gray-950/50">
                  <TileHeader className="border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <TileTitle className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                        Quiz
                      </TileTitle>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Top 5</span>
                    </div>
                  </TileHeader>
                  <TileContent>
                    {limitedQuizHighscores.length > 0 ? (
                      <div className="space-y-3">
                        {limitedQuizHighscores.map((entry, index) => (
                          <Tile
                            key={`quiz-${entry.username}-${index}`}
                            variant="compact"
                            className="cursor-pointer shadow-none dark:bg-gray-900"
                            onClick={() => {
                              void navigate('/namen-lernen/quiz')
                            }}
                          >
                            <TileContent className="flex items-center justify-between gap-3 py-3">
                              <div className="flex min-w-0 items-center gap-3">
                                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-sm font-semibold text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200">
                                  {index + 1}
                                </div>
                                <div className="min-w-0">
                                  <p className="truncate font-medium text-gray-900 dark:text-gray-50">{entry.username}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {typeof entry.correctAnswers === 'number' ? `Richtige: ${entry.correctAnswers}` : ''}
                                    {typeof entry.livesRemaining === 'number'
                                      ? `${typeof entry.correctAnswers === 'number' ? ' • ' : ''}Leben: ${entry.livesRemaining}`
                                      : ''}
                                    {entry.durationSeconds
                                      ? `${typeof entry.correctAnswers === 'number' || typeof entry.livesRemaining === 'number' ? ' • ' : ''}${entry.durationSeconds}s`
                                      : ''}
                                  </p>
                                </div>
                              </div>
                              <div className="shrink-0 text-right">
                                <p className="text-lg font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                                  {entry.score}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Punkte</p>
                              </div>
                            </TileContent>
                          </Tile>
                        ))}
                      </div>
                    ) : (
                      <p className="rounded-lg border border-dashed border-gray-200 bg-white p-4 text-center text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-400">
                        Keine Quiz-Highscores vorhanden
                      </p>
                    )}
                  </TileContent>
                </Tile>

                <Tile className="bg-gray-50 dark:bg-gray-950/50">
                  <TileHeader className="border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <TileTitle className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                        Snake
                      </TileTitle>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Top 5</span>
                    </div>
                  </TileHeader>
                  <TileContent>
                    {limitedSnakeHighscores.length > 0 ? (
                      <div className="space-y-3">
                        {limitedSnakeHighscores.map((entry, index) => (
                          <Tile
                            key={`snake-${entry.username}-${index}`}
                            variant="compact"
                            className="cursor-pointer shadow-none dark:bg-gray-900"
                            onClick={() => {
                              void navigate('/namen-lernen/snake')
                            }}
                          >
                            <TileContent className="flex items-center justify-between gap-3 py-3">
                              <div className="flex min-w-0 items-center gap-3">
                                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-sm font-semibold text-amber-800 dark:bg-amber-950/50 dark:text-amber-200">
                                  {index + 1}
                                </div>
                                <div className="min-w-0">
                                  <p className="truncate font-medium text-gray-900 dark:text-gray-50">{entry.username}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {entry.maxLength ? `Länge: ${entry.maxLength}` : ''}
                                    {entry.durationSeconds ? `${entry.maxLength ? ' • ' : ''}${entry.durationSeconds}s` : ''}
                                  </p>
                                </div>
                              </div>
                              <div className="shrink-0 text-right">
                                <p className="text-lg font-bold tabular-nums text-amber-600 dark:text-amber-400">
                                  {entry.score}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Punkte</p>
                              </div>
                            </TileContent>
                          </Tile>
                        ))}
                      </div>
                    ) : (
                      <p className="rounded-lg border border-dashed border-gray-200 bg-white p-4 text-center text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-400">
                        Keine Snake-Highscores vorhanden
                      </p>
                    )}
                  </TileContent>
                </Tile>
              </div>
              <TileFooter>
                <Link
                  to="/namen-lernen"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
                >
                  <Gamepad2 className="h-4 w-4" aria-hidden />
                  Jetzt spielen und neuen Highscore setzen!
                </Link>
              </TileFooter>
            </div>
          ) : (
            <EmptyState
              title="Noch keine Highscores vorhanden"
              description="Starten Sie ein Spiel und erscheinen Sie in der Bestenliste."
              action={
                <Button type="button" size="sm" onClick={() => navigate('/namen-lernen')}>
                  <Gamepad2 className="mr-2 h-4 w-4" aria-hidden />
                  Ersten Highscore setzen!
                </Button>
              }
            />
          )}
        </TileContent>
      </Tile>
            </div>
          )}
        </SortableListItem>
      )}

      {hasWidget('quickLinks') && (
        <SortableListItem id="quickLinks">
          {({ setNodeRef, style, dragHandleProps }) => (
            <div ref={setNodeRef} style={getWidgetContainerStyle('quickLinks', style)} className="space-y-3">
              {renderWidgetToolbar('quickLinks', dragHandleProps)}
      <Tile>
        <TileHeader>
          <TileTitle as="h2" className="text-base">
            Schnellzugriff
          </TileTitle>
        </TileHeader>
        <TileContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Link to="/students" className="block no-underline">
              <Tile variant="interactive" className="h-full">
                <TileContent className="flex items-center gap-3 py-5">
                  <Users className="h-6 w-6 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-50">Schüler verwalten</span>
                </TileContent>
              </Tile>
            </Link>
            <Link to="/attendance" className="block no-underline">
              <Tile variant="interactive" className="h-full">
                <TileContent className="flex items-center gap-3 py-5">
                  <Calendar className="h-6 w-6 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-50">Anwesenheit</span>
                </TileContent>
              </Tile>
            </Link>
            <Link to="/lernbuero" className="block no-underline">
              <Tile variant="interactive" className="h-full">
                <TileContent className="flex items-center gap-3 py-5">
                  <BookOpen className="h-6 w-6 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-50">Lernbüro</span>
                </TileContent>
              </Tile>
            </Link>
            <Link to="/building-blocks" className="block no-underline">
              <Tile variant="interactive" className="h-full">
                <TileContent className="flex items-center gap-3 py-5">
                  <TrendingUp className="h-6 w-6 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-50">Bausteine</span>
                </TileContent>
              </Tile>
            </Link>
          </div>
        </TileContent>
      </Tile>
            </div>
          )}
        </SortableListItem>
      )}
      </SortableVerticalList>

      <Modal
        isOpen={isWidgetCatalogOpen}
        onClose={() => setIsWidgetCatalogOpen(false)}
        title="Widgets verwalten"
        description="Fügen Sie ausgeblendete Widgets wieder hinzu oder setzen Sie das Standardlayout zurück."
        footer={
          <div className="flex justify-between gap-2">
            <Button variant="outline" onClick={handleWidgetReset} disabled={saveLayoutMutation.isLoading}>
              Standardlayout wiederherstellen
            </Button>
            <Button variant="outline" onClick={() => setIsWidgetCatalogOpen(false)}>
              Schließen
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          {addableWidgets.length === 0 ? (
            <EmptyState title="Alle Widgets sind bereits aktiv." />
          ) : (
            addableWidgets.map((widget) => (
              <div
                key={widget.id}
                className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700"
              >
                <div className="text-sm font-medium text-gray-800 dark:text-gray-100">{widget.title}</div>
                <Button
                  size="sm"
                  onClick={() => {
                    handleWidgetAdd(widget.id)
                  }}
                  disabled={saveLayoutMutation.isLoading}
                >
                  Hinzufügen
                </Button>
              </div>
            ))
          )}
        </div>
      </Modal>
    </div>
  )
}
